import type {
  DefaultSelectedItem,
  MenuOptions,
} from '@labrav/react-components/lib/@types/v2/components/NestedDropdown'
import type {
  MarketplaceFieldMappingPicklistData,
  MarketplaceHubspotFieldsData,
  MarketplaceHubspotFieldsOptions,
} from '../../../modules/marketplaceMappingManager/types'

export const hubspotFieldInputTypes = [
  'Text',
  'URL',
  'Email',
  'Phone',
  'Number',
  'Currency',
  'Array',
  'Boolean',
]

export const isCrmPicklistFieldType = (fieldType?: string): boolean =>
  fieldType === 'Picklist' || fieldType === 'Multipicklist'

export const isSelectedCrmPicklistField = ({
  hubspotField,
  hubspotObject,
  hubspotFieldData,
}: {
  hubspotField?: string
  hubspotObject?: string
  hubspotFieldData?: Array<{
    internalName?: string
    objectType?: string
    fieldType?: string
  }>
}): boolean => {
  if (!hubspotField || !hubspotObject || !hubspotFieldData?.length) {
    return false
  }
  return hubspotFieldData.some(
    item =>
      item.internalName === hubspotField &&
      item.objectType === hubspotObject &&
      isCrmPicklistFieldType(item.fieldType)
  )
}

export const getInputType = (referralFieldType: string) => {
  switch (referralFieldType) {
    case 'Text':
    case 'Array':
      return 'text'
    case 'URL':
      return 'url'
    case 'Email':
      return 'email'
    case 'Phone':
      return 'tel'
    case 'Number':
    case 'Currency':
      return 'number'
    default:
      return 'text'
  }
}

export const commonCosellMappingMenuData: MenuOptions[] = [
  {
    label: '--None--',
    value: '',
    options: [],
  },
  {
    label: 'Deals',
    value: 'Deals',
    options: [],
  },
  {
    label: 'Companies',
    value: 'Companies',
    options: [],
  },
  {
    label: 'Contacts',
    value: 'Contacts',
    options: [],
  },
  {
    label: 'Opportunity',
    value: 'Opportunity',
    options: [],
  },
  {
    label: 'Account',
    value: 'Account',
    options: [],
  },
]

export const MapTablePicklistMapping = {
  'Referral picklist values': 'referralPicklistValues',
  'Opportunity values': 'opportunityValues',
}

export type PicklistMappingUpdateAction = (
  fieldKey: string,
  picklistMappings: MarketplaceFieldMappingPicklistData[]
) => void

export type RenderNestedSelectProps = {
  menuData: MenuOptions[]
  mapButton?: boolean
  mapButtonDisabled?: boolean
  disabled?: boolean
  onSelectionChange?: (
    selectedValues: string[],
    selectedPaths: string[],
    hubspotObject?: string,
    isMapped?: boolean
  ) => void
  defaultSelectedItems?: DefaultSelectedItem[]
  hubspotField?: string
  hubspotObject?: string
  hubspotFieldData?: MarketplaceHubspotFieldsData[]
  fieldKey?: string
  picklistMappings?: MarketplaceFieldMappingPicklistData[]
  picklist?: MarketplaceHubspotFieldsOptions[]
  category?: string
  referralFieldType?: string
  isViewOnly?: boolean
  onPicklistMappingChange?: PicklistMappingUpdateAction
}

export function camelCaseToWords(str: string): string {
  if (!str) return ''
  const result = str.replace(/([A-Z])/g, ' $1').toLowerCase()
  return result.charAt(0).toUpperCase() + result.slice(1)
}

const NONE_CRM_MENU_OPTION: MenuOptions = {
  label: '--None--',
  value: '',
  options: [],
}

const HUBSPOT_CRM_OBJECT_TYPES = ['Companies', 'Contacts', 'Deals'] as const

const SALESFORCE_OBJECT_TYPE_FALLBACK = ['Account', 'Opportunity'] as const

/** Sort NestedDropdown options A–Z by label (recurses into nested options). */
export const sortCrmMenuOptionsByLabel = <
  T extends { label?: string; value?: string; options?: T[] },
>(
  options: T[]
): T[] =>
  [...options]
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    .map(option => ({
      ...option,
      options:
        Array.isArray(option.options) && option.options.length > 0
          ? sortCrmMenuOptionsByLabel(option.options)
          : ((option.options || []) as T[]),
    }))

/**
 * Ensures `--None--` is first, then sorts remaining options A–Z by label
 * (including nested children).
 */
export const withSortedNoneCrmMenuOption = (
  menuData: MenuOptions[]
): MenuOptions[] => {
  if (!menuData.length) return menuData
  const sortedRest = sortCrmMenuOptionsByLabel(
    menuData.filter(option => option.value !== '')
  )
  if (menuData[0].value === '') {
    return [menuData[0], ...sortedRest]
  }
  return [{ ...NONE_CRM_MENU_OPTION }, ...sortedRest]
}

/** Flat picklist / default-value options, sorted A–Z by label. */
export const sortCrmPicklistOptions = <
  T extends { label?: string; value?: string },
>(
  options: T[]
): T[] =>
  [...options].sort((a, b) =>
    (a.label || a.value || '').localeCompare(b.label || b.value || '')
  )

export const getSalesforceObjectTypes = (
  fields: Array<{ objectType?: string }>
): string[] => {
  const objectTypes = Array.from(
    new Set(
      fields
        .map(field => field.objectType)
        .filter((objectType): objectType is string => Boolean(objectType))
    )
  )
  if (!objectTypes.length) {
    return [...SALESFORCE_OBJECT_TYPE_FALLBACK].sort((a, b) =>
      a.localeCompare(b)
    )
  }
  return objectTypes.sort((a, b) => a.localeCompare(b))
}

type CrmMenuField = {
  displayLabel: string
  internalName: string
}

const buildCrmObjectMenuEntries = (
  objectTypes: string[],
  getMatchingFields: (objectType: string) => CrmMenuField[]
): MenuOptions[] =>
  objectTypes.map(objectType => ({
    label: objectType,
    value: objectType,
    options: [...getMatchingFields(objectType)]
      .sort((a, b) =>
        (a.displayLabel || a.internalName).localeCompare(
          b.displayLabel || b.internalName
        )
      )
      .map(field => ({
        label: field.displayLabel || field.internalName,
        value: field.internalName,
        options: [] as [],
      })),
  }))

/**
 * Builds HubSpot object/field menu for mapping rows.
 * Object roots default to Companies / Contacts / Deals (alphabetical);
 * nested fields are sorted A–Z by display label.
 */
export const buildHubspotCrmFieldMenuEntries = ({
  fieldType,
  getMatchingFields,
  objectTypes = [...HUBSPOT_CRM_OBJECT_TYPES],
}: {
  fieldType: string
  getMatchingFields: (
    objectType: string,
    fieldType: string
  ) => Array<{ displayLabel: string; internalName: string }>
  objectTypes?: readonly string[]
}): MenuOptions[] =>
  buildCrmObjectMenuEntries(
    [...objectTypes].sort((a, b) => a.localeCompare(b)),
    objectType => getMatchingFields(objectType, fieldType)
  )

/**
 * Full Salesforce NestedDropdown menu: `--None--` plus every object/field
 * from flattened CRM rows (objects and fields sorted A–Z).
 */
export const buildSalesforceCrmNestedMenu = (
  fields: Array<{
    objectType?: string
    displayLabel?: string
    internalName?: string
  }>
): MenuOptions[] => [
  { ...NONE_CRM_MENU_OPTION },
  ...buildCrmObjectMenuEntries(getSalesforceObjectTypes(fields), objectType =>
    fields
      .filter(field => field.objectType === objectType && field.internalName)
      .map(field => ({
        displayLabel: field.displayLabel || field.internalName || '',
        internalName: field.internalName || '',
      }))
  ),
]
